import Link from 'next/link';
import React from 'react';
import Icon, { IconType } from '@shared/components/Icon/Icon';
import {
    TypeButtonLinkComponentSkeleton,
    TypeMegaMenuComponentWithoutUnresolvableLinksResponse,
} from '@shared/features/contentful/_generated';
import { resolveContentfulTargetProps } from '@shared/features/contentful/linkMap';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Entry } from 'contentful';
import { useSettingsContext } from '@shared/contexts/SettingsContext';
import Image from 'next/image';
import { APP_STORE_BADGE_SIZE } from '@shared/components/Icon/appStoreBadgeIcons';
interface IFooterDownloadAppProps {
    apps?: TypeMegaMenuComponentWithoutUnresolvableLinksResponse['fields']['appLinks'];
    priority?: boolean;
    contentfulEntryId: string;
}

export const FooterDownloadAppLink = ({
    priority = false,
    app,
    iconClassName,
}: {
    app: Entry<TypeButtonLinkComponentSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>;
    priority?: boolean;
    iconClassName?: string;
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: app.sys.id });
    const { getLabelsForCommonKey } = useSettingsContext();

    const { downloadOnTheAppStore, downloadOnGooglePlay } = getLabelsForCommonKey('labels', [
        'downloadOnTheAppStore',
        'downloadOnGooglePlay',
    ]);

    const getAppIconAltText = (iconType: string | undefined, title?: string) => {
        if (iconType === 'App Store') {
            return downloadOnTheAppStore;
        }
        if (iconType === 'Google Play') {
            return downloadOnGooglePlay;
        }

        return title || `${iconType || 'app'} icon`;
    };

    const getIcon = (
        app: Entry<TypeButtonLinkComponentSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>,
    ) => {
        switch (app?.fields?.icon as IconType) {
            case 'App Store (Light)':
                return 'App Store';
            case 'Google Play (Light)':
                return 'Google Play';
            default:
                return app?.fields?.icon as IconType;
        }
    };

    const iconType = getIcon(app);

    return (
        <Link
            key={app.sys.id}
            href={app.fields.link ?? '/'}
            role="link"
            aria-label={app.fields.title ?? app.fields.name}
            {...resolveContentfulTargetProps(app.fields.target)}
            className="outline-offset-[8px]"
        >
            {app.fields.icon !== 'other' ? (
                <Icon
                    priority={priority}
                    type={iconType as IconType}
                    alt={getAppIconAltText(app.fields.icon, app.fields.title)}
                    ariaLabel={getAppIconAltText(app.fields.icon, app.fields.title)}
                    {...(iconClassName ? { className: iconClassName } : APP_STORE_BADGE_SIZE)}
                    {...inspectorProps({ fieldId: 'title' })}
                />
            ) : (
                app.fields.media?.fields.file?.url && (
                    <Image
                        src={`https:${app.fields.media?.fields.file?.url}`}
                        alt={app.fields.title ?? ''}
                        width={10}
                        height={10}
                        priority={priority}
                        className="h-[40px] w-full"
                        {...inspectorProps({ fieldId: 'media' })}
                    />
                )
            )}
        </Link>
    );
};

const FooterDownloadApp: React.FC<IFooterDownloadAppProps> = ({
    // priority,
    apps,
    contentfulEntryId,
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: contentfulEntryId });
    const { getLabel } = useSettingsContext();
    const labelDownloadOurApp = getLabel(['footer', 'downloadOurApp']);
    return (
        <div
            {...inspectorProps({
                fieldId: 'appLinks',
            })}
        >
            <p
                className="block text-sm font-medium text-colourContainerTertiary mb-6"
                {...inspectorProps({ fieldId: 'settings' })}
            >
                {labelDownloadOurApp}
            </p>
            <div className="grid grid-cols-2 min-[500px]:flex gap-2">
                {apps?.map(
                    (app) =>
                        app && (
                            <FooterDownloadAppLink
                                priority
                                app={app}
                                key={app.sys.id}
                                iconClassName="w-full h-auto min-[500px]:w-auto min-[500px]:h-[40px]"
                            />
                        ),
                )}
            </div>
        </div>
    );
};

export default FooterDownloadApp;
